import { Link } from "react-router-dom";
import { ArrowUpRight, Facebook, Instagram, Mail, MessageCircle, Phone } from "lucide-react";
import { AREAS, CONTACTS, LOGO_URL, waLink } from "@/data/content";
import { Reveal } from "@/components/Reveal";

const CARDS = [
  {
    id: "whatsapp",
    icon: MessageCircle,
    label: "WhatsApp",
    value: CONTACTS.phoneDisplay,
    href: waLink(),
    external: true,
  },
  {
    id: "email",
    icon: Mail,
    label: "Email",
    value: CONTACTS.email,
    href: `mailto:${CONTACTS.email}`,
  },
];

const PHONES = [
  { display: CONTACTS.phoneDisplay, href: CONTACTS.phoneHref, testid: "contact-phone-link-1" },
  { display: CONTACTS.phone2Display, href: CONTACTS.phone2Href, testid: "contact-phone-link-2" },
];

export default function Footer() {
  return (
    <footer id="contactos" data-testid="contact-section" className="scroll-mt-24 bg-pine-deep text-ivory">
      <div className="mx-auto max-w-7xl px-5 pb-10 pt-20 sm:pt-28 lg:px-10">
        <div className="flex flex-col items-start justify-between gap-10 lg:flex-row lg:items-end">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-lime-400">Contactos</p>
            <h2 className="mt-4 max-w-xl font-serif text-3xl leading-tight tracking-tight sm:text-4xl lg:text-5xl">
              Vamos tratar do seu <span className="italic text-lime-300">espaço verde</span>?
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex gap-3">
              <a
                data-testid="social-instagram-link"
                href={CONTACTS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram da EcoJard"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-pine-line text-ivory/80 transition-colors hover:border-lime-400 hover:text-lime-300"
              >
                <Instagram size={18} />
              </a>
              <a
                data-testid="social-facebook-link"
                href={CONTACTS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook da EcoJard"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-pine-line text-ivory/80 transition-colors hover:border-lime-400 hover:text-lime-300"
              >
                <Facebook size={18} />
              </a>
            </div>
          </Reveal>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Reveal>
            <div
              data-testid="contact-phone-card"
              className="flex h-full items-center gap-4 rounded-2xl border border-pine-line bg-white/[0.04] px-6 py-5 transition-colors duration-300 hover:bg-white/[0.08]"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-lime-400/15 text-lime-400">
                <Phone size={19} />
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-ivory/50">Telefone</p>
                {PHONES.map((p) => (
                  <a
                    key={p.href}
                    data-testid={p.testid}
                    href={p.href}
                    className="mt-0.5 block text-sm font-semibold transition-colors hover:text-lime-300 sm:text-base"
                  >
                    {p.display}
                  </a>
                ))}
              </div>
            </div>
          </Reveal>
          {CARDS.map((c, i) => (
            <Reveal key={c.id} delay={0.08 * i}>
              <a
                data-testid={`contact-${c.id}-card`}
                href={c.href}
                {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                className="group flex items-center justify-between gap-4 rounded-2xl border border-pine-line bg-white/[0.04] px-6 py-5 transition-colors duration-300 hover:bg-white/[0.08]"
              >
                <div className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-lime-400/15 text-lime-400">
                    <c.icon size={19} />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-ivory/50">{c.label}</p>
                    <p className="mt-0.5 text-sm font-semibold sm:text-base">{c.value}</p>
                  </div>
                </div>
                <ArrowUpRight size={18} className="text-lime-400 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
              </a>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-6 border-t border-pine-line pt-8 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="EcoJard" className="h-9 w-auto rounded-md bg-ivory px-2 py-1" />
            <span className="text-sm text-ivory/60">
              Jardinagem e manutenção de espaços verdes
            </span>
          </div>
          <p className="text-xs text-ivory/50">
            {AREAS.map((a) => a.name).join(" · ")}
          </p>
          <p className="text-xs text-ivory/50">
            © {new Date().getFullYear()} EcoJard ·{" "}
            <Link
              to="/privacidade"
              data-testid="footer-privacy-link"
              className="underline-offset-4 transition-colors hover:text-lime-300 hover:underline"
            >
              Política de Privacidade
            </Link>
          </p>
        </div>
      </div>
    </footer>
  );
}
